<template>
    <div class="">
        <div class="row">
            <div class="col-12 col-md-6">
                <therapist-profile
                    :organization="organization"
                    :id="therapist_.id"
                    :person="therapist_.user"
                    :therapist="therapist_"
                    :can-edit-user="canEditUser"
                    :can-edit-therapist="canEditStaff"
                    @data-saved="getTherapist()"
                ></therapist-profile>

                <!--CLINIC INFO-->
                <therapist-clinic-information
                    :organization="organization"
                    :id="therapist_.id"
                    :therapist="therapist_"
                    :therapist-types="therapistTypes"
                    :supervisors="supervisors"
                    :can-edit-user="canEditUser"
                    :can-edit-therapist="canEditStaff"
                    @data-saved="getTherapist()"
                ></therapist-clinic-information>
            </div>
            <div class="col-12 col-md-6">
                <!--EMERGENCY INFO-->
                <emergency-info
                    :person="therapist_"
                    :endpoint="`/api/organizations/${organization.id}/therapists/`"
                    :contacts-property="`emergency_contacts`"
                    @person-saved="getTherapist()"
                ></emergency-info>
                <!--HOURS-->
                <section>
                    <data-dump-widget
                        ref="widgetHours"
                        :key="widgetHours.key"
                        :parent-key="widgetHours.key"
                        :title="widgetHours.title"
                        :fields="widgetHours.fields"
                        :payload="widgetHours.payload"
                        :data-source="hoursDataSource"
                        :action-title="canEditStaff ? widgetHours.actionTitle : null"
                        :endpoint="therapistEndpoint"
                        @edit-data="editDataWidget('widgetHours')"
                        @data-saved="getTherapist()"
                        @reset="resetPayload('widgetHours')"
                    ></data-dump-widget>
                </section>

                <!--SPECIAL CONDITIONS-->
                <section class="mt-3 widget bg-white border-light p-3">
                    <a
                        class="font-weight-bold float-right "
                        :class="
                            canEditStaff
                                ? 'text-primary cursor-pointer'
                                : 'text-muted cursor-not-allowed'
                        "
                        @click="canEditStaff ? (specialConditionsVisible = true) : null"
                        >Edit</a
                    >

                    <h5 class="mb-3 text-uppercase font-weight-bold">Special Conditions</h5>

                    <p class="mb-0">
                        <span class="font-weight-bold">Special Conditions:</span>
                        <span v-for="s in therapist_.special_conditions" class="comma-list">{{
                            s.description
                        }}</span>
                    </p>

                    <modal-component
                        :id="`modal-special-conditions`"
                        :title="`Edit Special Conditions`"
                        :visible="specialConditionsVisible"
                        @close="specialConditionsVisible = false"
                    >
                        <div class="p-3">
                            <p class="mb-0" v-for="o in specialConditionsOptions">
                                <input
                                    type="checkbox"
                                    class="mr-2"
                                    :value="o.value"
                                    v-model="specialConditionsSelected"
                                />
                                <span>{{ o.title }}</span>
                            </p>
                            <button
                                class="btn btn-sm btn-primary text-uppercase font-weight-bold text-white float-right"
                                @click="saveSpecialConditions()"
                            >
                                Save
                            </button>
                        </div>
                    </modal-component>
                </section>
            </div>
        </div>

        <transition-group name="squish-vert-fade">
            <section :key="'pay-schedule'" v-if="['development'].includes($env) && canViewPayroll">
                <table-input-widget
                    ref="widgetPaySchedule"
                    :key="widgetPaySchedule.key"
                    :title="widgetPaySchedule.title"
                    :fields="widgetPaySchedule.fields"
                    :data-source="paySchedules"
                    :errors="widgetPaySchedule.errors"
                    table-class="small"
                    @edit="
                        canEditPayroll ? editTableWidgetEntry('widgetPaySchedule', $event) : null
                    "
                >
                    <template slot="bottom" v-if="canEditPayroll">
                        <div class="my-2 row">
                            <div
                                v-for="f in widgetPaySchedule.fields"
                                :class="`col-${f.widthBase12}`"
                            >
                                <multi-field
                                    :parent-key="widgetPaySchedule.key"
                                    :name="f.prop"
                                    v-model="widgetPaySchedule.payload[f.prop]"
                                    @focus="setMainAction('widgetPaySchedule', 'save')"
                                    :type="f.type"
                                ></multi-field>
                            </div>
                            <div class="col-1 px-1">
                                <input-actions-widget
                                    :widget="widgetPaySchedule"
                                    :main="widgetPaySchedule.mainAction"
                                    @cancel-widget-edit="cancelWidgetEdit('widgetPaySchedule')"
                                    @save-widget-new-entry="saveWidgetNewEntry('widgetPaySchedule')"
                                    @delete-widget-entry="deleteWidgetEntry('widgetPaySchedule')"
                                ></input-actions-widget>
                            </div>
                        </div>
                    </template>
                </table-input-widget>
            </section>

            <section :key="'credentials'" class="mb-5">
                <table-input-widget
                    ref="widgetCredentials"
                    :key="widgetCredentials.key"
                    :title="widgetCredentials.title"
                    :fields="widgetCredentials.fields"
                    :data-source="therapistCredentials"
                    :errors="widgetCredentials.errors"
                    table-class="small"
                    @edit="editTableWidgetEntry('widgetCredentials', $event)"
                >
                    <template slot="bottom">
                        <div class="my-2 row">
                            <div class="col-3">
                                <select
                                    class="form-control form-control-sm"
                                    v-model="widgetCredentials.payload.credential_id"
                                    @focus="setMainAction('widgetCredentials', 'save')"
                                >
                                    <option :value="null" hidden selected disabled
                                        >Choose one</option
                                    >
                                    <option
                                        v-for="o in unusedCredentials"
                                        :value="o.value"
                                        :disabled="o.disabled"
                                        :selected="o.selected"
                                        :hidden="o.hidden"
                                        >{{ o.title }}</option
                                    >
                                </select>
                            </div>
                            <div class="col-2">
                                <div
                                    class="d-flex flex-column justify-content-start align-items-start"
                                >
                                    <v-date-picker
                                        class="form-control form-control-sm"
                                        popover-align="right"
                                        v-model="widgetCredentials.payload.issued_at"
                                        @input="setMainAction('widgetCredentials', 'save')"
                                    ></v-date-picker>
                                    <span class="small font-italic text-muted">*Optional</span>
                                </div>
                            </div>
                            <div class="col-2">
                                <div
                                    class="d-flex flex-column justify-content-start align-items-start"
                                >
                                    <v-date-picker
                                        class="form-control form-control-sm"
                                        popover-align="right"
                                        v-model="widgetCredentials.payload.expires_at"
                                        @input="setMainAction('widgetCredentials', 'save')"
                                    ></v-date-picker>
                                    <span class="small font-italic text-muted">*Optional</span>
                                </div>
                            </div>
                            <div class="col-3">
                                <div
                                    class="d-flex flex-column justify-content-start align-items-start"
                                >
                                    <textarea
                                        @focus="setMainAction('widgetCredentials', 'save')"
                                        class="form-control"
                                        name="notes"
                                        id="credential-notes"
                                        rows="1"
                                        v-model="widgetCredentials.payload.notes"
                                    ></textarea>
                                </div>
                            </div>
                            <div class="col-1 px-1">
                                <input-actions-widget
                                    :widget="widgetCredentials"
                                    :main="widgetCredentials.mainAction"
                                    @cancel-widget-edit="cancelWidgetEdit('widgetCredentials')"
                                    @save-widget-new-entry="saveWidgetNewEntry('widgetCredentials')"
                                    @delete-widget-entry="deleteWidgetEntry('widgetCredentials')"
                                ></input-actions-widget>
                            </div>
                        </div>
                    </template>
                </table-input-widget>
            </section>
        </transition-group>

        <internal-notes
            v-if="canViewStaffNotes"
            :api-endpoints="internalNotesEndpoint"
            :user-type="'therapist'"
        ></internal-notes>
    </div>
</template>

<script>
import TherapistProfile from "./TherapistProfile";
import TherapistClinicInformation from "./TherapistClinicInformation";
import DataDumpWidget from "../ui/DataDumpWidget";
import TableInputWidget from "../ui/TableInputWidget";
import EmergencyInfo from "../widgets/EmergencyInfoWidget";
import MultiField from "../ui/MultiField";
import InputActionsWidget from "../ui/InputActionsWidget";
import InternalNotes from "../ui/InternalNotes";
import ModalComponent from "../ui/ModalComponent";
import { fixDatePickers } from "../../plugins/fixDatePickers";
import { permissions } from "../../plugins/permissions";

export default {
    name: "TherapistOverview",
    props: ["organization", "user", "therapist", "credentials", "specialConditions"],
    mixins: [fixDatePickers, permissions],
    components: {
        TherapistProfile,
        TherapistClinicInformation,
        InternalNotes,
        DataDumpWidget,
        TableInputWidget,
        MultiField,
        InputActionsWidget,
        ModalComponent,
        EmergencyInfo,
    },
    data() {
        return {
            therapist_: {},
            widgetHours: {
                key: "hours",
                title: "Hours",
                prop: "therapist_",
                fields: [
                    { title: "Expected hours", prop: "expected_hours", type: "number" },
                    { title: "Max hours", prop: "max_hours", type: "number" },
                    {
                        title: "Max overtime hours",
                        prop: "max_hours_get_paid_overtime",
                        type: "number",
                    },
                    {
                        title: "Supervised hours needed",
                        prop: "supervised_hours_needed",
                        type: "number",
                    },
                    {
                        title: "Vacation time allotted",
                        prop: "vacation_time_allotted",
                        type: "number",
                    },
                    { title: "Sick time allotted", prop: "sick_time_allotted", type: "number" },
                ],
                payload: {},
                actionTitle: "Edit information",
                endpoint: `/api/organizations/{organizationId}/therapists/{therapistId}`,
                errors: {},
            },
            specialConditionsVisible: false,
            specialConditionsOptions: [],
            specialConditionsSelected: [],
            widgetPaySchedule: {
                key: "pay-schedule",
                title: "Pay Schedule",
                prop: "paySchedules",
                fields: [
                    { title: "Type", prop: "type", widthBase12: 2, type: "text" },
                    { title: "Code", prop: "code", widthBase12: 1, type: "text" },
                    { title: "Name", prop: "name", widthBase12: 2, type: "text" },
                    {
                        title: "Effective Date",
                        prop: "effective_date",
                        widthBase12: 2,
                        type: "date",
                    },
                    { title: "End Date", prop: "end_date", widthBase12: 2, type: "date" },
                    { title: "$/hr", prop: "per_hour_cost", widthBase12: 1, type: "text" },
                    { title: "Cap yr", prop: "per_hour_cap_year", widthBase12: 1, type: "text" },
                ],
                endpoint: `/api/organizations/{organizationId}/therapists/{therapistId}/pay_schedules`,
                payload: {},
                errors: {},
                mainAction: "add",
            },
            paySchedules: [],
            widgetCredentials: {
                key: "credentials",
                title: "Credentials",
                prop: "therapistCredentials",
                fields: [
                    {
                        title: "Credential",
                        prop: "credential_id",
                        widthBase12: 3,
                        type: "select",
                        options: [],
                        displayProp: ["type", "degree"],
                    },
                    { title: "Issued date", prop: "issued_at", widthBase12: 2, type: "date" },
                    { title: "Expire date", prop: "expires_at", widthBase12: 2, type: "date" },
                    { title: "Notes", prop: "notes", widthBase12: 3, type: "text" },
                ],
                endpoint: `/api/organizations/{organizationId}/therapists/{therapistId}/credentials`,
                payload: {
                    credential_id: null,
                },
                errors: {},
                mainAction: "add",
            },
            therapistCredentials: [],
            supervisors: [],
            therapistTypes: [],
            errors: {},
        };
    },
    computed: {
        unusedCredentials() {
            return this.widgetCredentials.fields[0].options.filter(
                opt =>
                    !this.therapistCredentials
                        .map(c => parseInt(c.id))
                        .includes(parseInt(opt.value))
            );
        },

        therapistEndpoint() {
            return `/api/organizations/${this.organization.id}/therapists/${this.therapist.id}`;
        },

        internalNotesEndpoint() {
            if (this.organization && this.therapist) {
                return {
                    get: `/api/organizations/${this.organization.id}/therapists/${
                        this.therapist.id
                    }/notes`,
                    post: `/api/organizations/${this.organization.id}/therapists/${
                        this.therapist.id
                    }/notes`,
                };
            }
            return {};
        },

        specialConditionsDataSource() {
            let dataSource = { ...this.therapist_ };
            let specialConditionsSelected = [];
            dataSource.special_conditions.forEach(c => {
                specialConditionsSelected.push(c.id);
            });
            dataSource.special_conditions = specialConditionsSelected;
            return this.filterByFields(dataSource, this.widgetSpecialConditions.fields);
        },

        hoursDataSource() {
            let dataSource = { ...this.therapist_ };
            return this.filterByFields(dataSource, this.widgetHours.fields);
        },

        canEditUser() {
            return this.user.id === this.therapist.user.id;
        },
    },
    created() {
        this.therapist_ = { ...this.therapist };
        this.populateSpecialConditions();
    },
    watch: {
        organization() {
            if (this.organization && this.organization.id) {
                this.initialize();
            }
        },
        credentials() {
            let credentialsSelect = [];
            //credentialsSelect.push({value: null, title: 'Choose one'});
            this.credentials.forEach(c => {
                const credential = {};
                credential.value = c.id.toString();
                credential.title = `${c.type} - ${c.degree}`;
                credentialsSelect.push(credential);
            });
            this.widgetCredentials.fields.find(
                f => f.prop === "credential_id"
            ).options = credentialsSelect;
        },
        specialConditions() {
            let specialConditionsSelect = [];
            this.specialConditions.forEach(c => {
                const specialCondition = {};
                specialCondition.value = c.id;
                specialCondition.title = `${c.description}`;
                specialConditionsSelect.push(specialCondition);
            });
            this.specialConditionsOptions = specialConditionsSelect;
        },
    },
    methods: {
        //Setup

        initialize() {
            this.fetchTherapistTypes();
            this.fetchSupervisors();
            ["widgetPaySchedule", "widgetCredentials"].forEach(w => {
                this.populateWidgetEndpoint(w);
                this.getWidgetDataSource(w);
            });
        },

        resetPayload(widgetName) {
            this[widgetName].payload = {};
        },

        populateSpecialConditions() {
            let specialConditionsSelected = [];
            this.therapist_.special_conditions.forEach(c => {
                specialConditionsSelected.push(c.id);
            });
            this.specialConditionsSelected = specialConditionsSelected;
        },

        populateWidgetEndpoint(widgetName) {
            this[widgetName].endpoint = this[widgetName].endpoint.replace(
                "{organizationId}",
                this.organization.id
            );
            this[widgetName].endpoint = this[widgetName].endpoint.replace(
                "{therapistId}",
                this.therapist.id
            );
        },

        getWidgetDataSource(widgetName) {
            this[widgetName].loading = true;
            axios({
                method: "GET",
                url: this[widgetName].endpoint.replace("organizationId", this.organization.id),
            })
                .then(r => {
                    this[widgetName].loading = false;
                    this[this[widgetName].prop] = this.treatSpecificWidgetsAfterFetch(
                        widgetName,
                        r.data.data
                    );
                })
                .catch(e => {
                    this[widgetName].loading = false;
                    console.error(e);
                });
        },

        fetchTherapistTypes() {
            axios
                .get(`/api/organizations/${this.organization.id}/therapist_types`)
                .then(r => {
                    this.therapistTypes = r.data.data;
                })
                .catch(e => {
                    console.error(e);
                });
        },

        fetchSupervisors() {
            axios
                .get(`/api/organizations/${this.organization.id}/users`)
                .then(r => {
                    this.supervisors = r.data.data;
                })
                .catch(e => {
                    console.error(e);
                });
        },

        treatSpecificWidgetsAfterFetch(widgetName, data) {
            switch (widgetName) {
                case "widgetCredentials":
                    this[widgetName].payload.credential_id = null;
                    return data;
                default:
                    return data;
            }
        },

        getTherapist() {
            axios
                .get(`/api/organizations/${this.organization.id}/therapists/${this.therapist.id}/`)
                .then(r => {
                    this.therapist_ = r.data.data;
                    this.populateSpecialConditions();
                })
                .catch(e => {
                    console.error(e);
                });
        },

        // Edit data

        editTableWidgetEntry(widgetName, id) {
            this.setMainAction(widgetName, "cancel");
            let payload = this[this[widgetName].prop].find(w => w.id === id);
            payload = this.treatSpecificWidgetsBeforeEdit(widgetName, payload);
            if (payload.cantEdit) return;
            this[widgetName].payload = payload;
        },

        editDataWidget(widgetName) {
            const dataSource = this.$refs[widgetName].dataSource;
            this[widgetName].payload = this.treatSpecificWidgetsBeforeEdit(widgetName, {
                ...dataSource,
            });
        },

        cancelWidgetEdit(widgetName) {
            this.setMainAction(widgetName, "add");
            this.$refs[widgetName].resetSelected();
            this[widgetName].payload = {};
        },

        treatSpecificWidgetsBeforeEdit(widgetName, payload) {
            switch (widgetName) {
                case "widgetPaySchedule":
                    payload.effective_date = new Date(payload.effective_date);
                    payload.end_date = new Date(payload.end_date);
                    break;
                case "widgetSpecialConditions":
                    payload = { special_conditions: [...this.therapist_.special_conditions] }; //Override
                    let special_conditions = [];
                    payload.special_conditions.forEach(s => {
                        special_conditions.push(`${s.id}`);
                    });
                    payload.special_conditions = special_conditions;
                    break;
                case "widgetCredentials":
                    payload.credential_id = this.credentials.find(
                        c => c.degree === payload.degree
                    ).id;
                    payload.expires_at = payload.expires_at
                        ? new Date(payload.expires_at)
                        : payload.expires_at;
                    payload.issued_at = payload.issued_at
                        ? new Date(payload.issued_at)
                        : payload.issued_at;
                    break;
                default:
                    return payload;
            }
            return payload;
        },

        // Save data

        treatSpecificWidgetsBeforeSave(widgetName) {
            let payload = { ...this[widgetName].payload };
            switch (widgetName) {
                case "widgetPaySchedule":
                    payload.effective_date = this.moment(payload.effective_date).format(
                        "YYYY-MM-DD HH:mm:ss"
                    );
                    payload.end_date = this.moment(payload.effective_date).format(
                        "YYYY-MM-DD HH:mm:ss"
                    );
                    break;
                case "widgetCredentials":
                    const changeCredentialId = parseInt(payload.credential_id) !== payload.id;
                    if (changeCredentialId) {
                        axios.delete(this[widgetName].endpoint + "/" + payload.id).catch(e => {
                            console.error(e);
                            this[widgetName].errors = e.response.data.errors;
                        });
                    }
                    if (changeCredentialId) {
                        delete payload.id;
                    }
                    payload.expires_at = payload.expires_at
                        ? this.moment(payload.expires_at).format("YYYY-MM-DD HH:mm:ss")
                        : null;
                    payload.issued_at = payload.issued_at
                        ? this.moment(payload.issued_at).format("YYYY-MM-DD HH:mm:ss")
                        : null;
                    payload.credential_id = parseInt(payload.credential_id);

                    Object.keys(payload).forEach(k => {
                        if (!["id", ...this.widgetCredentials.fields.map(f => f.prop)].includes(k))
                            delete payload[k];
                    });
                    break;
                default:
                    return payload;
            }
            return payload;
        },

        saveSpecialConditions() {
            let payload = { special_conditions: [...this.specialConditionsSelected] };
            axios
                .patch(
                    `/api/organizations/${this.organization.id}/therapists/${this.therapist.id}`,
                    payload
                )
                .then(r => {
                    this.getTherapist();
                    this.specialConditionsVisible = false;
                })
                .catch(e => {
                    console.error(e);
                });
        },

        saveWidgetNewEntry(widgetName) {
            this[widgetName].errors = {};
            let payload = this.treatSpecificWidgetsBeforeSave(widgetName);
            if (!["widgetCredentials"].includes(widgetName) && payload.id) {
                //For credentials, we don't want to remove values set to empty
                payload = this.cleanEmptyProperties(payload);
            }
            if (!payload) return;
            axios({
                method:
                    payload.id || ["widgetSpecialConditions"].includes(widgetName)
                        ? "PATCH"
                        : "POST",
                url: this[widgetName].endpoint + (payload.id ? `/${payload.id}` : ""),
                data: payload,
            })
                .then(r => {
                    this.$refs[widgetName].resetSelected();
                    this[widgetName].payload = {};
                    this.getWidgetDataSource(widgetName);
                    this.setMainAction(widgetName, "add");
                })
                .catch(e => {
                    console.error(e);
                    this[widgetName].errors = e.response.data.errors;
                });
        },

        // Delete data

        deleteWidgetEntry(widgetName) {
            axios({
                method: "DELETE",
                url:
                    this[widgetName].endpoint.replace("organizationId", this.organization.id) +
                    "/" +
                    this[widgetName].payload.id,
            })
                .then(r => {
                    this.$refs[widgetName].resetSelected();
                    this[widgetName].payload = {};
                    this.getWidgetDataSource(widgetName);
                    this.setMainAction(widgetName, "add");
                })
                .catch(e => {
                    console.error(e);
                });
        },

        // Helpers

        cleanEmptyProperties(payload) {
            Object.keys(payload).forEach(k => {
                if (!(payload[k] === 0) && !payload[k]) {
                    delete payload[k];
                }
            });
            return payload;
        },

        filterByFields(source, fields) {
            let fieldNames = [];
            fields.forEach(f => fieldNames.push(f.prop));
            return Object.keys(source)
                .filter(key => fieldNames.includes(key))
                .reduce((obj, key) => {
                    obj[key] = source[key];
                    return obj;
                }, {});
        },

        setMainAction(widgetName, action = "add") {
            this[widgetName].mainAction = action;
        },
    },
};
</script>

<style scoped lang="scss"></style>
